<script lang="ts" setup>
import { useFilterRegistration } from "@/composables/useFilterRegistry"
import type { Account } from "@/model/app/account"
import type { LoginAccess } from "@/model/app/login-access"
import { injectMandatory, useAppConfig, useHttpClient, type ModuleConfiguration } from "@/vf"
import { ref, watch, type Ref } from "vue"
import VueMultiselect from "vue-multiselect"

const props = defineProps<{
    customerView: boolean
}>()

const { appConfig } = useAppConfig()
const http = useHttpClient()
const selectedAccount = ref(null)

useFilterRegistration("organization", {
    isActive: () => selectedAccount.value !== null,
    reset() {
        selectedAccount.value = null
    },
})

const config = injectMandatory<Ref<ModuleConfiguration>>(
    "module-config",
    "module-config is required for FilterOrganizationsDropdown",
)

watch(selectedAccount, (account: Account | null) => {
    if (account) {
        config.value.list.requestParams.value = {
            ...(config.value.list.requestParams.value ?? {}),
            accountId: account.id!,
        }
    } else {
        config.value.list.requestParams.value = {
            ...(config.value.list.requestParams.value ?? {}),
            accountId: "",
        }
    }
})

const accounts: Ref<Account[]> = ref([])
if (props.customerView) {
    appConfig.value.accesses.sort((a: LoginAccess, b: LoginAccess) => {
        const nameA = a.account?.name?.toUpperCase() ?? "" // ignore upper and lowercase
        const nameB = b.account?.name?.toUpperCase() ?? "" // ignore upper and lowercase
        if (nameA < nameB) {
            return -1
        }
        if (nameA > nameB) {
            return 1
        }

        // names must be equal
        return 0
    })
    for (const access of appConfig.value.accesses) {
        accounts.value.push(access.account)
    }
}

async function asyncFind(query: string) {
    if (!props.customerView) {
        accounts.value = (
            await http.get<Account[]>("/account-search", {
                name: query,
            })
        ).data
    }
}
</script>
<template>
    <div class="d-inline-flex ml-4 mr-4">
        <VueMultiselect
            v-model="selectedAccount"
            :options="accounts"
            :multiple="false"
            :taggable="false"
            @search-change="asyncFind"
            placeholder="..."
            label="name"
            track-by="id"
            style="width: 300px"
        >
            <template #singleLabel="props">
                <i
                    class="d-inline-block fa text-center mr-2"
                    style="width: 1.5rem"
                    :class="{
                        'fa-user-tie': props.option.type === 'Admin',
                        'fa-user-circle': props.option.type === 'User',
                        'fa-building': props.option.type === 'Organization',
                        'fa-users': props.option.type === 'StaffMember',
                        'fa-building-user': props.option.type === 'OrganizationMember',
                    }"
                ></i>
                {{ props.option.name }}
            </template>
            <template #option="props">
                <i
                    class="d-inline-block fa text-center mr-2"
                    style="width: 1.5rem"
                    :class="{
                        'fa-user-tie': props.option.type === 'Admin',
                        'fa-user-circle': props.option.type === 'User',
                        'fa-building': props.option.type === 'Organization',
                        'fa-users': props.option.type === 'StaffMember',
                        'fa-building-user': props.option.type === 'OrganizationMember',
                    }"
                ></i>
                {{ props.option.name }}
            </template>
        </VueMultiselect>
    </div>
</template>
<style src="vue-multiselect/dist/vue-multiselect.css"></style>
